import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function PatternGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);

  const patterns = [
    ['🔴', '🔵'],
    ['⭐', '🌙'],
    ['🍎', '🍊'],
    ['🦁', '🐯'],
    ['🌸', '🌻'],
    ['🔷', '🔶'],
    ['🎈', '🎁'],
    ['🦋', '🐝']
  ];

  const generateQuestion = () => {
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    const patternLength = state.age <= 7 ? 4 : state.age <= 9 ? 6 : 8;
    
    // Create the pattern sequence
    const sequence = [];
    for (let i = 0; i < patternLength; i++) {
      sequence.push(pattern[i % pattern.length]);
    }
    
    // Remove the last element to create the question
    const correctAnswer = sequence[sequence.length - 1];
    const questionSequence = sequence.slice(0, -1);
    
    // Generate wrong answers from the same pattern and other patterns
    const wrongAnswers = [];
    
    // Add the other element from the same pattern
    const otherFromPattern = pattern.find(item => item !== correctAnswer);
    if (otherFromPattern) wrongAnswers.push(otherFromPattern);
    
    // Add random elements from other patterns
    while (wrongAnswers.length < 3) {
      const randomPattern = patterns[Math.floor(Math.random() * patterns.length)];
      const randomElement = randomPattern[Math.floor(Math.random() * randomPattern.length)];
      if (!wrongAnswers.includes(randomElement) && randomElement !== correctAnswer) {
        wrongAnswers.push(randomElement);
      }
    }
    
    const allAnswers = [correctAnswer, ...wrongAnswers.slice(0, 3)].sort(() => Math.random() - 0.5);
    
    return {
      sequence: questionSequence,
      correctAnswer,
      answers: allAnswers,
      pattern
    };
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [state.age]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    
    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: 15 });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }
    
    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);
      
      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🔄</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Pattern Master!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            You completed the Pattern Quest! You're amazing at spotting patterns!
          </p>
          
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <h1 className="text-3xl font-kid text-jungle-700">Pattern Quest 🔄</h1>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-purple-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-purple-400 to-purple-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-6">
            Complete the pattern!
          </h2>

          {/* Pattern Display */}
          <div className="flex justify-center items-center mb-8 space-x-4 flex-wrap">
            {currentQuestion.sequence.map((item, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: index * 0.2 }}
                className="text-5xl m-2"
              >
                {item}
              </motion.div>
            ))}
            
            {/* Question mark */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: currentQuestion.sequence.length * 0.2 }}
              className="text-5xl m-2 bg-safari-200 rounded-full w-16 h-16 flex items-center justify-center border-4 border-dashed border-safari-400"
            >
              ?
            </motion.div>
          </div>

          {/* Pattern Hint */}
          <div className="text-center mb-6">
            <p className="font-comic text-jungle-600 text-lg">
              Look at the pattern: {currentQuestion.pattern.join(' → ')} → {currentQuestion.pattern.join(' → ')}...
            </p>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentQuestion.answers.map((answer, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => !showResult && handleAnswerSelect(answer)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-kid text-4xl shadow-lg transition-all duration-200 ${
                  showResult
                    ? answer === currentQuestion.correctAnswer
                      ? 'bg-green-400 text-white'
                      : answer === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                {answer}
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <div className="text-green-600 font-kid text-xl">
                    🎉 Perfect! You found the pattern!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl">
                    Good try! The next item in the pattern is {currentQuestion.correctAnswer}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default PatternGame;